import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';

import { InjectedIntl } from 'react-intl';

import { getCookie } from '@atlassian/browser-storage-controls';
import { CompassTab } from '@atlassian/product-search-dialog';
import { Targets } from '@atlassiansox/cross-flow-support';

import urls from '../../urls';
import { FlagGroupItem } from '../flag-group';

import { HorizontalNavContext } from './horizontal-nav-context';
import messages from './horizontal-platform-search.i18n';

const HorizontalPlatformSearchCompassTabUpsell = ({
  flags,
  intl,
  isWorkspaceAdmin,
  onRetry,
  preSetFlagCallback,
  setFlags,
  workspaceSlug,
}: {
  flags: FlagGroupItem[];
  intl: InjectedIntl;
  isWorkspaceAdmin: boolean;
  onRetry: () => void;
  /**
   * Optional prop to run a callback before setting the flag.
   *
   * Used to workaround a UI bug caused by FlagGroup that blocks the default
   * behavior of clicking outside the search dropdown to close it.
   */
  preSetFlagCallback?: () => void;
  setFlags: Dispatch<SetStateAction<FlagGroupItem[]>>;
  workspaceSlug: string;
}) => {
  const [emailRequestLoading, setEmailRequestLoading] = useState(false);
  const [emailSendSuccess, setEmailSendSuccess] = useState(false);
  const { publishUiEvent } = useContext(HorizontalNavContext);

  const sendEmailNotificationToAdmin = useCallback(async () => {
    setEmailRequestLoading(true);

    const addFlag = ({ ...flagProps }: Omit<FlagGroupItem, 'id'>) => {
      setFlags(current => [{ ...flagProps, id: flags.length }, ...current]);
    };

    try {
      const token = getCookie('csrftoken');

      const response = await fetch(
        urls.api.internal.compassBannerEmailNotification(workspaceSlug),
        {
          method: 'POST',
          credentials: 'same-origin',
          // For required headers, see: src/utils/fetch.ts
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-Bitbucket-Frontend': 'frontbucket',
            'X-CSRFToken': token || '',
          },
          body: JSON.stringify({
            target_product: Targets.COMPASS,
            source_component: 'crossProductSearch',
            source_context: 'bitbucket',
            journey: 'get-started',
            provisioning_page_options: {
              actions: {
                primary: {
                  behaviour: 'land',
                  prompt: 'go-to-product',
                  options: {
                    targetTab: 'new',
                    urlParams: {
                      onboarding: 'bitbucket',
                    },
                  },
                },
              },
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error();
      }

      setEmailSendSuccess(true);

      preSetFlagCallback?.();
      addFlag({
        title: intl.formatMessage(messages.accessRequestSentHeading),
        description: intl.formatMessage(messages.accessRequestSentDescription),
      });
    } catch {
      preSetFlagCallback?.();
      addFlag({
        hasError: true,
        title: intl.formatMessage(messages.accessRequestSentHeadingError),
        description: intl.formatMessage(
          messages.accessRequestSentDescriptionError
        ),
      });
    } finally {
      setEmailRequestLoading(false);

      publishUiEvent({
        action: 'sent',
        actionSubject: 'email',
        actionSubjectId: 'crossProductSearchRequestCompassFree',
        attributes: {
          success: emailSendSuccess,
          workspaceAdmin: false,
        },
        source: 'crossProductSearch',
      });
    }
  }, [
    emailSendSuccess,
    flags.length,
    intl,
    publishUiEvent,
    setFlags,
    preSetFlagCallback,
    workspaceSlug,
  ]);

  const ctaOverrideProps = isWorkspaceAdmin
    ? undefined
    : {
        children: intl.formatMessage(messages.compassRequestAccess),
        onClick: sendEmailNotificationToAdmin,
        isLoading: emailRequestLoading,
        isDisabled: emailSendSuccess,
      };

  return (
    <CompassTab
      crossFlowEnabled
      isCompassActivated={false}
      onRetry={onRetry}
      order={1}
      sourceContext="bitbucket"
      upsellCtaButtonProps={ctaOverrideProps}
      userTeamConfig={{
        context: {
          contextType: 'filter',
          principalId: 'Context',
          productKey: 'compass',
          siteId: 'bitbucket-cloud',
        },
      }}
    />
  );
};

export default React.memo(HorizontalPlatformSearchCompassTabUpsell);
